/**
 * useSpellCheckOnSave — drop-in UK English validator for any admin save flow.
 *
 * Wraps three pieces of state + three handlers that any admin editor can
 * use to gate its save on the UK-English validator:
 *
 *   const sc = useSpellCheckOnSave({
 *     buildPayload: () => ({ title, body, ... }),
 *     persist: async (cleaned) => { await axios.put(...); }
 *   });
 *
 *   // Replace your existing save() call with:
 *   <Button onClick={sc.save}>Save</Button>
 *
 *   // And mount the dialog once in your component:
 *   <SpellCheckDialog {...sc.dialogProps} />
 *
 * Behaviour matches the original EditPageContent.js flow:
 *   - Run POST /api/admin/spell-check first.
 *   - If 0 issues → call persist() immediately.
 *   - If >0 issues → open the dialog; user picks Apply all (cleaned save)
 *     or Save as-is (original save). Either way persist() is called once.
 *   - If the spell-check endpoint errors → best-effort: log and persist
 *     the original (don't block the admin from saving).
 */
import { useCallback, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useSpellCheckOnSave({ buildPayload, persist }) {
  const [findings, setFindings] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = useCallback(async () => {
    const payload = buildPayload();
    try {
      const r = await axios.post(
        `${API}/admin/spell-check`,
        { payload },
        { withCredentials: true },
      );
      if (r.data?.total_issues > 0) {
        setFindings(r.data.findings);
        return;
      }
    } catch (err) {
      console.warn('Spell-check failed, saving without it:', err);
    }
    await persist(payload);
  }, [buildPayload, persist]);

  const applyFixesAndSave = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/spell-check/apply`,
        { payload: buildPayload() },
        { withCredentials: true },
      );
      setFindings(null);
      await persist(r.data?.payload);
    } catch (err) {
      console.warn('Spell-check apply failed:', err);
    } finally {
      setBusy(false);
    }
  }, [buildPayload, persist]);

  const saveAsIs = useCallback(async () => {
    setBusy(true);
    try {
      setFindings(null);
      await persist(buildPayload());
    } finally {
      setBusy(false);
    }
  }, [buildPayload, persist]);

  return {
    save,
    busy,
    dialogProps: {
      open: !!findings,
      findings: findings || [],
      busy,
      onApplyAll: applyFixesAndSave,
      onSaveAsIs: saveAsIs,
      onOpenChange: (open) => { if (!open) setFindings(null); },
    },
  };
}
